import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { ShareButton, FavoriteButton } from '../components/ShareButton';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Trophy, 
  MapPin, 
  Calendar, 
  Users, 
  DollarSign,
  Search,
  Building,
  Mail,
  Phone,
  Clock,
  ChevronRight,
  Globe,
  Map,
  Flag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];

const LEVEL_INFO = {
  district: {
    label: 'District',
    icon: MapPin,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    description: 'Local tournaments in your area'
  },
  regional: {
    label: 'Regional',
    icon: Map,
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    description: 'Multi-district regional competitions'
  },
  state: {
    label: 'State',
    icon: Flag,
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    description: 'State-wide championship tournaments'
  },
  national: {
    label: 'National',
    icon: Globe,
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    description: 'National level competitions'
  }
};

export const DiscoverTournaments = () => {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState({
    district: [],
    regional: [],
    state: [],
    national: []
  });
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchTournaments();
  }, [ageFilter, genderFilter]);

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (ageFilter !== 'All') params.append('age_category', ageFilter);
      if (genderFilter !== 'All') params.append('gender', genderFilter);
      if (searchLocation) params.append('location', searchLocation);
      
      const response = await axios.get(`${API}/tournaments/discover/upcoming?${params}`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchTournaments();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTotalCount = () => {
    return Object.values(tournaments).reduce((sum, arr) => sum + arr.length, 0);
  };

  const TournamentCard = ({ tournament }) => {
    const levelInfo = LEVEL_INFO[tournament.level] || LEVEL_INFO.district;
    const LevelIcon = levelInfo.icon;
    
    return (
      <Card className="card-hover overflow-hidden" data-testid={`tournament-${tournament.id}`}>
        <div className={`h-1 ${
          tournament.level === 'district' ? 'bg-blue-500' :
          tournament.level === 'regional' ? 'bg-green-500' :
          tournament.level === 'state' ? 'bg-purple-500' :
          'bg-orange-500'
        }`} />
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <Badge variant="outline" className={levelInfo.color}>
              <LevelIcon className="w-3 h-3 mr-1" />
              {levelInfo.label}
            </Badge>
            <Badge variant="secondary">
              {tournament.age_category} • {tournament.gender}
            </Badge>
          </div>
          
          <h3 className="font-bold text-lg mb-2 line-clamp-1">{tournament.name}</h3>
          
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {tournament.description}
          </p>
          
          <div className="space-y-2 text-sm">
            {tournament.location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tournament.location}</span>
              </div>
            )}
            
            {tournament.venue_name && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tournament.venue_name}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{tournament.registered_teams}/{tournament.max_teams} teams</span>
              </div>
              <div className="flex items-center gap-1 font-semibold text-primary">
                <DollarSign className="w-4 h-4" />
                {tournament.entry_fee}
              </div>
            </div>
          </div>
          
          {tournament.registration_deadline && (
            <div className="mt-3 p-2 rounded bg-destructive/10 text-destructive text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Registration deadline: {formatDate(tournament.registration_deadline)}
            </div>
          )}
          
          <div className="mt-4 pt-3 border-t flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              By: <span className="font-medium">{tournament.organizer_name || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2">
              {user && <FavoriteButton tournamentId={tournament.id} />}
              <ShareButton tournamentId={tournament.id} tournamentName={tournament.name} />
              <Link to={`/tournaments/${tournament.id}`}>
                <Button size="sm" variant="outline" className="gap-1">
                  View <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const TournamentSection = ({ level, tournamentList }) => {
    const levelInfo = LEVEL_INFO[level];
    const LevelIcon = levelInfo.icon;
    
    if (tournamentList.length === 0) return null;
    
    return (
      <div className="mb-8" data-testid={`${level}-section`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${levelInfo.color}`}>
            <LevelIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{levelInfo.label} Tournaments</h2>
            <p className="text-sm text-muted-foreground">{levelInfo.description}</p>
          </div>
          <Badge variant="secondary" className="ml-auto">
            {tournamentList.length} upcoming
          </Badge>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tournamentList.map(tournament => (
            <TournamentCard key={tournament.id} tournament={tournament} />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="discover-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black">
          <span className="text-primary">DISCOVER TOURNAMENTS</span>
        </h1>
        <p className="text-muted-foreground mt-1">
          Find upcoming flag football tournaments from directors across all levels
        </p>
      </div>

      {/* Search & Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by city or state..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="location-search"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="age-filter">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={handleSearch} className="btn-primary" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Level Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(LEVEL_INFO).map(([level, info]) => {
          const LevelIcon = info.icon;
          const count = tournaments[level]?.length || 0;
          return (
            <Card 
              key={level}
              className={`cursor-pointer transition-all hover:shadow-md ${activeTab === level ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setActiveTab(level)}
              data-testid={`stat-${level}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info.color}`}>
                    <LevelIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-black">{count}</p>
                    <p className="text-xs text-muted-foreground">{info.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({getTotalCount()})
          </TabsTrigger>
          <TabsTrigger value="district" data-testid="tab-district">
            District ({tournaments.district?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="regional" data-testid="tab-regional">
            Regional ({tournaments.regional?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="state" data-testid="tab-state">
            State ({tournaments.state?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="national" data-testid="tab-national">
            National ({tournaments.national?.length || 0})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="all">
          {getTotalCount() > 0 ? (
            <>
              <TournamentSection level="district" tournamentList={tournaments.district || []} />
              <TournamentSection level="regional" tournamentList={tournaments.regional || []} />
              <TournamentSection level="state" tournamentList={tournaments.state || []} />
              <TournamentSection level="national" tournamentList={tournaments.national || []} />
            </>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Upcoming Tournaments</h3>
                <p className="text-muted-foreground">
                  Check back later or adjust your filters
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {['district', 'regional', 'state', 'national'].map(level => (
          <TabsContent key={level} value={level}>
            {(tournaments[level]?.length || 0) > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tournaments[level].map(tournament => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="text-xl font-bold mb-2">No {LEVEL_INFO[level].label} Tournaments</h3>
                  <p className="text-muted-foreground">
                    No upcoming {LEVEL_INFO[level].label.toLowerCase()} tournaments found
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
